import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import Badge from "../../components/common/Badge/Badge";
import Button from "../../components/common/Button/Button";
import TabsNavigation from "../../components/common/TabsNavigation/TabsNavigation";
import Table from "../../components/common/Table/Table";
import { getApiInstance } from "../../utils/api";
import "./FichaCentroProductor.scss";
import "../informes/cantidadesGeneradas/CantidadesGeneradas.scss";
import DetalleLayout from "../../Layouts/DetalleLayout/DetalleLayout";
import InputDropdown from "../../components/inputs/InputDropdown/InputDropdown_New";
import { useZone } from "../../context/ZoneContext";
import { CiClock1 } from "react-icons/ci";
import { CgDanger } from "react-icons/cg";
import { CiCircleCheck } from "react-icons/ci";
import { IoTrashOutline, IoLockOpenOutline, IoStopOutline, IoReloadOutline, IoClose, IoImageOutline } from "react-icons/io5";
import {
    addOrUpdateRecentItem,
    mapList,
    mappedDataToFormData,
} from "../../utils/utils";
import { formatDate } from "../../utils/utils";
import PieChartGraph from "../../components/common/Charts/PieChartGraph";
import { getConfig } from "../../utils/config";
import ScrollableBarChartGraph from "../../components/common/Charts/ScrollableBarChartGraph";
import { FiFileText, FiDownload, FiEdit } from "react-icons/fi";
import { IoMdAdd } from "react-icons/io";
import Modal from "../../components/common/Modal/Modal";
import ValidacionesHistorico from "../../components/common/ValidacionesHistorico/ValidacionesHistorico";
import ResiduoDetalleModal from "../../components/layout/CentrosProductores/ResiduoDetalleModal";
import Notification from "../../components/common/notification/notification";
import { useRef, useMemo } from "react";

import {
    formatKg,
    formatPeso,
    formatPesoPorTipo,
    formatPesoResiduo,
    pesoAKilogramos,
    pesoEnUnidadResiduo,
    unidadResiduo,
} from "../../utils/format";
import { formateadorMoneda } from "../../utils/moneda";
import { useMoneda } from "../../context/MonedaContext";

/* Lo que GetObra trae de cada línea de residuo para pintar la ficha y que el
   guardado no usa. En multipart un objeto viaja como "[object Object]", y el
   documento en base64 puede pasar del límite de 4 MB por campo del servidor. */
const CAMPOS_RESIDUO_SOLO_LECTURA = [
    "residuo",
    "transportista",
    "gestorFinal",
    "gestorIntermedio",
    "tipoTratamientoIntermedio",
    "tipoTratamientoFinal",
    "documentoTransporteBase64",
    "documentoFinalBase64",
];

const obtenerEstiloDeProgreso = (valor, maximo) => {
    if (maximo === 0) {
        return {
            "--progress-color": "#f44336",
        };
    }

    const porcentaje = (valor / maximo) * 100;
    let color;

    if (porcentaje < 40) {
        color = "#f44336";
    } else if (porcentaje < 75) {
        color = "#ff9800";
    } else {
        color = "#4caf50";
    }

    return {
        "--progress-color": color,
    };
};

/**
 * Agrupa los residuos de un centro por tipo de residuo y calcula lo que necesitan
 * la pestaña de cantidades (barras, tarta) y la tabla de agrupados.
 *
 * Estaba dentro de la carga inicial de la ficha, así que al validar o editar un
 * residuo la tabla de generados cambiaba pero los agrupados y las gráficas se
 * quedaban con los datos de la primera carga.
 */
const calcularDerivadosResiduos = (residuos, codObra) => {
    const residuosGrouped = [];
    residuos.forEach((currentItem) => {
        if (!currentItem?.residuo) return;

        /* La valorización (kg) la calcula el backend según el tratamiento final,
           igual que el informe de Toneladas Valorizadas: si el tratamiento es de
           eliminación vale 0 aunque Porcentaje2 sea 100, porque Porcentaje2 es el
           % que va a ese tratamiento, no el % valorizado. Viene vacía cuando la
           línea no tiene tratamiento final o porcentaje: eso es "sin dato". */
        const conDatoValorizacion = currentItem.valorizacion != null;
        const valorizacionKg = conDatoValorizacion
            ? Number(currentItem.valorizacion) || 0
            : 0;

        let group = residuosGrouped.find(
            (g) => g.codResiduo === currentItem.codResiduo
        );

        if (!group) {
            group = {
                codObra,
                codResiduo: currentItem.codResiduo,
                codTipoResiduo: currentItem.codTipoResiduo,
                residuo:
                    currentItem.residuo.codigo != null &&
                    currentItem.residuo.codigo !== "" &&
                    currentItem.residuo.codigo !== undefined
                        ? currentItem.residuo.codigo +
                          " - " +
                          currentItem.residuo.nombre
                        : currentItem.residuo.nombre,
                peso: 0,
                pesoConDatoValorizacion: 0,
                pesoSinDatoValorizacion: 0,
                valorizacion: 0,
                costeTotal: 0,
            };

            residuosGrouped.push(group);
        }

        group.peso += currentItem.peso;
        group.costeTotal += currentItem.costeTotal;

        if (conDatoValorizacion) {
            group.pesoConDatoValorizacion += currentItem.peso;
            group.valorizacion += valorizacionKg;
        } else {
            group.pesoSinDatoValorizacion += currentItem.peso;
        }
    });

    const datosBarChart = residuosGrouped
        .map((group) => {
            const valorizacion = group.valorizacion || 0;
            const pctValorizacion =
                group.peso > 0
                    ? (valorizacion / group.peso) * 100
                    : 0;

            return {
                name: group.residuo,
                "Cantidad generada": group.peso,
                "Valorización": valorizacion,
                pctValorizacion,
                /* Lo usa el formateador de la barra para decidir kg o tn. */
                peligroso: [1, 3, 5].includes(Number(group.codTipoResiduo)),
            };
        })
        .sort(
            (a, b) =>
                b["Cantidad generada"] +
                b["Valorización"] -
                (a["Cantidad generada"] + a["Valorización"])
        );

    const totalValorizado = residuosGrouped.reduce(
        (sum, group) => sum + (group.valorizacion || 0),
        0
    );
    const totalEliminado = residuos.reduce(
        (sum, item) => sum + (item.eliminacion || 0),
        0
    );
    const totalGenerado = residuosGrouped.reduce(
        (sum, group) => sum + (group.peso || 0),
        0
    );
    const granTotal = totalValorizado + totalEliminado;
    const porcVal =
        granTotal > 0 ? (totalValorizado / granTotal) * 100 : 0;
    const porcElim =
        granTotal > 0 ? (totalEliminado / granTotal) * 100 : 0;

    const datosProcesadosPie = [
        {
            name: "Valorización",
            value: totalValorizado,
        },
        {
            name: "Eliminación",
            value: totalEliminado,
        },
    ];

    return {
        residuosGrouped,
        datosBarChart,
        datosProcesadosPie,
        totalGenerado,
        totalValorizado,
        totalEliminado,
        porcVal,
        porcElim,
    };
};

export default function FichaCentroProductor({ user }) {
    const { id } = useParams();
    const [centro, setCentro] = useState(null);
    const [documentos, setDocumentos] = useState([]);
    const [cumplimientoLegal, setCumplimientoLegal] = useState([]);
    const [residuos, setResiduos] = useState(null);
    const [residuosAgrupados, setResiduosAgrupados] = useState([]);
    const [residuosGenerados, setResiduosGenerados] = useState([]);
    const [tarifas, setTarifas] = useState([]);
    const [imagenes, setImagenes] = useState([]);
    const [activeTabDatos, setActiveTabDatos] = useState("informacion");
    const [activeTabResiduos, setActiveTabResiduos] = useState("cantidades");
    const [activeTabDocumentacion, setActiveTabDocumentacion] =
        useState("documentacion");
    const [loading, setLoading] = useState(true);
    const [exportando, setExportando] = useState(false);
    const [error, setError] = useState(null);
    const [openDropdownRowId, setOpenDropdownRowId] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);
    const [modalEdicionAbierto, setModalEdicionAbierto] = useState(false);
    const [formEdicionCentro, setFormEdicionCentro] = useState({});
    const { selectedZone } = useZone();
    const { moneda } = useMoneda();
    const [distribuidoras, setDistribuidoras] = useState([]);
    const [consumo, setConsumo] = useState([]);
    const [proyectos, setProyectos] = useState([]);
    const [transportistas, setTransportistas] = useState([]);
    const [vehiculos, setVehiculos] = useState([]);
    const [combustibles, setCombustibles] = useState([]);
    const [gestores, setGestores] = useState([]);
    const [tiposTratamiento, setTiposTratamiento] = useState([]);
    const [residuosObraGroupped, setResiduosObraGroupped] = useState([]);
    const [datosProcesadosBar, setDatosProcesadosBar] = useState([]);
    const [datosProcesadosPie, setDatosProcesadosPie] = useState([]);
    const [totalCantidadGenerada, setTotalCantidadGenerada] = useState(0);
    const [totalValorizacion, setTotalValoracion] = useState(0);
    const [totalEliminacion, setTotalEliminacion] = useState(0);
    const [porcentajeValorizacion, setPorcentajeValorizacion] = useState(0);
    const [porcentajeEliminacion, setPorcentajeEliminacion] = useState(0);
    const [tiposDocumentosObligatorios, setTiposDocumentosObligatorios] =
        useState([]);
    const [tiposDocumentosNoObligatorios, setTiposDocumentosNoObligatorios] =
        useState([]);
    const [isModalBitacoraOpen, setIsModalBitacoraOpen] = useState(false);
    const [selectedMonthBitacora, setSelectedMonthBitacora] = useState(null);
    const [selectedBitacoraRow, setSelectedBitacoraRow] = useState(null);

    const [isHistoricoOpen, setIsHistoricoOpen] = useState(false);
    const [historicoValidacion, setHistoricoValidacion] = useState(null);
    const [tiposPorResiduo, setTiposPorResiduo] = useState({});
    const [residuoDetalle, setResiduoDetalle] = useState(null);

    /* Peligrosos en kilos, no peligrosos en toneladas: la unidad del formulario
       depende del residuo elegido, igual que en la aplicación antigua. El backend
       recibe y devuelve siempre kilos, así que se convierte al entrar y al salir
       del formulario. */
    const tipoDeResiduo = (codResiduo) => tiposPorResiduo[String(codResiduo)];

    const etiquetaPesoResiduo = (data) =>
        `Peso (${unidadResiduo(tipoDeResiduo(data?.codResiduo))})`;

    /* SetResiduoObra y UpdateResiduoObra solo aceptan multipart, pero la tabla
       manda JSON cuando no se adjunta ningún documento: el servidor respondía 415
       y no se podía editar solo una cantidad. Por eso aquí se envía siempre
       FormData.

       Si sí hay documento, la tabla ya entrega un FormData. Antes el peso no se
       convertía en ese caso y los no peligrosos se guardaban en tn en vez de kg;
       ahora se pasa a kg en los dos casos. */
    const residuoFormAPayload = (payload) => {
        const entradas =
            payload instanceof FormData
                ? Array.from(payload.entries())
                : Object.entries(payload ?? {});

        const datos = Object.fromEntries(
            entradas.filter(
                ([clave, valor]) =>
                    !CAMPOS_RESIDUO_SOLO_LECTURA.includes(clave) &&
                    // Un fichero (Blob o File) sí se envía; cualquier otro objeto no.
                    !(
                        valor !== null &&
                        typeof valor === "object" &&
                        !(valor instanceof Blob)
                    )
            )
        );

        if (datos.peso != null && datos.peso !== "") {
            datos.peso = pesoAKilogramos(
                datos.peso,
                tipoDeResiduo(datos.codResiduo)
            );
        }

        return mappedDataToFormData(datos);
    };

    /* CosteTotal lo calcula el backend sumando los seis conceptos, y la
       valorización sale del tratamiento final: al editar una línea no basta con
       refrescar en local lo que se envió, hay que volver a pedir la obra. De paso
       se recalculan los agrupados y las gráficas, que derivan de esta lista. */
    const recargarResiduos = async () => {
        try {
            const obra = await fetchCentroProductor(id);

            if (!Array.isArray(obra?.residuos)) obra.residuos = [];

            setCentro(obra);
            setResiduosGenerados(obra.residuos);
        } catch (error) {
            console.error("No se pudieron recargar los residuos:", error);
        }
    };

    /* El abono de chatarra es un ingreso: la aplicación antigua rechazaba el
       formulario si venía en positivo, con este mismo mensaje. */
    const validaResiduoForm = (data) => {
        const abono = data?.abonoChatarra;

        if (abono !== "" && abono != null && Number(abono) > 0) {
            return "El abono de chatarra debe ser un número negativo.";
        }

        return null;
    };

    /* "Tipo de tratamiento" (Eliminación / Valorización) no es un campo del residuo:
       solo existe en el formulario para acotar el desplegable de "Descripción tipo
       tratamiento", que es el que se guarda en codTipoTratamiento. Al editar hay que
       deducirlo del tratamiento guardado, porque si el padre viene vacío
       calculateAssociatedOptions deja el hijo sin opciones y la descripción aparece
       en blanco aunque esté guardada.

       Las opciones de tiposTratamiento llevan el tipo en `fatherId` (lo pone mapList),
       así que basta buscar por el código. */
    const tipoDeTratamiento = (codTipoTratamiento) => {
        if (codTipoTratamiento == null) return null;

        const opcion = (tiposTratamiento ?? []).find(
            (o) => String(o.value ?? o.id) === String(codTipoTratamiento)
        );

        return opcion?.fatherId ?? null;
    };

    const residuoFilaAFormulario = (row) => ({
        ...row,
        peso: pesoEnUnidadResiduo(row.peso, row.codTipoResiduo),
        tipoTratamiento:
            row.tipoTratamientoIntermedio?.tipo ??
            tipoDeTratamiento(row.codTipoTratamiento),
        tipoTratamiento2:
            row.tipoTratamientoFinal?.tipo ??
            tipoDeTratamiento(row.codTipoTratamiento2),
    });

    const config = getConfig();
    const isMabe = config?.REACT_APP_IS_MABE === "true";

    const puedeGestionarCentro = Number(user?.codAcceso) === 1;

    /* Reglas de la aplicación antigua (Views/Obras/Details.cshtml):
       sobre un centro cerrado (2) o incorrecto (7) no se toca nada; validar es
       solo de administradores (perfiles 1 y 2) y el histórico lo ven además los
       intermediarios (perfil 4). */
    const centroBloqueado =
        centro?.codEstado === 2 || centro?.codEstado === 7;
    const puedeEditarResiduos = puedeGestionarCentro && !centroBloqueado;
    const puedeValidar =
        puedeEditarResiduos &&
        (Number(user?.role) === 1 || Number(user?.role) === 2);
    const puedeVerValidaciones = [1, 2, 4].includes(Number(user?.role));

    /* GetObra devuelve los costes ya convertidos a la moneda del selector, y con
       ella el codigo aplicado. Se usa ese y no el del selector directamente: si el
       backend normaliza a otra (moneda no valida), el simbolo seguiria cuadrando
       con las cifras. Mientras no ha respondido, la del selector. */
    const monedaCentro = centro?.moneda ?? moneda;
    const formateaCoste = useMemo(
        () => formateadorMoneda(monedaCentro, { maximumFractionDigits: 2 }),
        [monedaCentro]
    );

    const abrirHistoricoValidaciones = (codTipoValidacion) => (row) => {
        setHistoricoValidacion({
            codTipoValidacion,
            codClave:
                row.codResiduoObra ?? row.codDocumento ?? row.CodClave ?? null,
        });
        setIsHistoricoOpen(true);
    };

    const notificationRef = useRef(null);
    const consumoTableRef = useRef(null);

    const formElementsDocumentos = [
        {
            elements: [
                {
                    name: "codTipoDocumento",
                    type: "radio",
                    label: "Tipo de documento",
                    placeholder: "Seleccione el tipo de documento",
                    required: true,
                    disabled: false,
                    labelVisible: true,
                    visible: true,
                    options: [
                        {
                            label: "Documentación obligatoria",
                            options: tiposDocumentosObligatorios,
                        },
                        {
                            label: "Documentacion no obligatoria",
                            options: tiposDocumentosNoObligatorios,
                        },
                    ],
                },
                {
                    name: "fichero",
                    type: "document",
                    label: "Documento",
                    placeholder: "Subir documento",
                    required: true,
                    disabled: false,
                    labelVisible: true,
                    visible: true,
                },
                {
                    name: "observaciones",
                    type: "textarea",
                    label: "Observaciones",
                    placeholder: "Escriba las observaciones aquí",
                    required: false,
                    disabled: false,
                    labelVisible: true,
                    visible: true,
                },
                {
                    name: "codObra",
                    type: "text",
                    label: "Código Obra",
                    placeholder: "",
                    required: false,
                    disabled: true,
                    labelVisible: false,
                    visible: false,
                    defaultValue: id,
                },
            ],
        },
    ];

    useEffect(() => {
        if (centro != null) {
            addOrUpdateRecentItem({
                id: centro.codObra,
                title: centro.nombre,
                type: "centroProductor",
            });
        }
    }, [centro]);

    /* Los agrupados y las gráficas son datos derivados de los residuos generados:
       se recalculan solos cuando la tabla cambia (al añadir, editar, borrar o
       validar un residuo), sin volver a pedir la ficha entera. */
    useEffect(() => {
        const derivados = calcularDerivadosResiduos(residuosGenerados, id);

        setResiduosObraGroupped(derivados.residuosGrouped);
        setDatosProcesadosBar(derivados.datosBarChart);
        setDatosProcesadosPie(derivados.datosProcesadosPie);
        setTotalCantidadGenerada(derivados.totalGenerado);
        setTotalValoracion(derivados.totalValorizado);
        setTotalEliminacion(derivados.totalEliminado);
        setPorcentajeValorizacion(derivados.porcVal);
        setPorcentajeEliminacion(derivados.porcElim);
    }, [residuosGenerados, id]);

    useEffect(() => {
        let isMounted = true;
        setLoading(true);
        setError(null);

        const loadAll = async () => {
            try {
                const obra = await fetchCentroProductor(id);

                if (!Array.isArray(obra?.residuos)) {
                    obra.residuos = [];
                }

                if (isMounted) {
                    setCentro(obra);
                    setResiduosGenerados(obra.residuos);
                }


                const results = await Promise.allSettled([
                    fetchDocumentosCentroProductor(id),
                    fetchElectricas(),
                    fetchConsumo(id),
                    fetchResiduos(),
                    fetchProyectos(),
                    fetchResiduosTiposTratamiento(),
                    fetchTiposVehiculos(),
                    fetchTiposCombustible(),
                    fetchImagenes(id),
                    fetchTarifas(id),
                    fetchTiposDocumentos(),
                    fetchProveedores(),
                ]);

                const [
                    documentosResult,
                    electricasResult,
                    consumoResult,
                    residuosResult,
                    proyectosResult,
                    tiposTratamientoResult,
                    vehiculosResult,
                    combustiblesResult,
                    imagenesResult,
                    tarifasResult,
                    tiposDocumentosResult,
                    proveedoresResult,
                ] = results;

                if (isMounted) {
                    if (documentosResult.status === "fulfilled") {
                        setDocumentos(documentosResult.value);
                    } else {
                        console.warn(
                            "No se pudieron cargar los documentos:",
                            documentosResult.reason
                        );
                    }

                    if (electricasResult.status === "fulfilled") {
                        setDistribuidoras(
                            mapList(
                                electricasResult.value,
                                "nombre",
                                "codElectrica"
                            )
                        );
                    } else {
                        console.warn(
                            "No se pudieron cargar las eléctricas:",
                            electricasResult.reason
                        );
                    }

                    if (consumoResult.status === "fulfilled") {
                        setConsumo(consumoResult.value);
                    } else {
                        console.warn(
                            "No se pudo cargar el consumo:",
                            consumoResult.reason
                        );
                    }

                    if (residuosResult.status === "fulfilled") {
                        /* El tipo de cada residuo decide la unidad del peso. El mapa
                           se construye con TODOS los residuos, no solo los de la
                           zona: si una línea ya guardada usa un residuo de otra
                           zona, al editarla hay que seguir sabiendo su unidad. */
                        setTiposPorResiduo(
                            Object.fromEntries(
                                residuosResult.value.map((r) => [
                                    String(r.codResiduo),
                                    r.codTipoResiduo,
                                ])
                            )
                        );

                        /* Solo los residuos de la zona del centro productor, como
                           la aplicación antigua, que cargaba la lista con
                           GetResiduosByZona(CodZona). */
                        const residuosElegibles = residuosResult.value.filter(
                            (r) => Number(r.codZona) === Number(obra.codZona)
                        );

                        setResiduos(
                            mapList(residuosElegibles, "nombre", "codResiduo").map(item => {
                                const residuo = residuosElegibles.find(
                                    r => r.codResiduo === item.id
                                );

                                const nombre =
                                    residuo?.codigo && residuo.codigo !== "-"
                                        ? `${residuo.codigo} - ${item.label}`
                                        : item.label;

                                return {
                                    ...item,
                                    /* El asterisco marca los peligrosos, como en
                                       el selector de la aplicación antigua. */
                                    label:
                                        unidadResiduo(
                                            residuo?.codTipoResiduo
                                        ) === "kg"
                                            ? `* ${nombre}`
                                            : nombre,
                                };
                            })
                        );
                    } else {
                        console.warn(
                            "No se pudieron cargar los residuos:",
                            residuosResult.reason
                        );
                    }

                    if (proyectosResult.status === "fulfilled") {
                        setProyectos([
                            ...mapList(
                                proyectosResult.value,
                                "proyecto",
                                "codProyecto"
                            ),
                        ]);
                    } else {
                        console.warn(
                            "No se pudieron cargar los proyectos:",
                            proyectosResult.reason
                        );
                    }

                    if (proveedoresResult.status === "fulfilled") {
                        setTransportistas([
                            ...mapList(
                                proveedoresResult.value.filter(
                                    (x) => x.isTransportista && x.codEstado == 3 && x.codPais === obra.codPais
                                ),
                                "nombre",
                                "codProveedor",
                                "codResiduo",
                                "codsResiduos"
                            ),
                        ]);
                        setGestores([
                            ...mapList(
                                proveedoresResult.value.filter(
                                    (x) => x.isGestorFinal && x.codEstado == 3 && x.codPais === obra.codPais
                                ),
                                "nombre",
                                "codProveedor",
                                "codResiduo",
                                "codsResiduos"
                            ),
                        ]);
                    }

                    if (tiposTratamientoResult.status === "fulfilled") {
                        setTiposTratamiento([
                            ...mapList(
                                tiposTratamientoResult.value,
                                ["codigo", "descripcion"],
                                "codTipoTratamiento",
                                "codTipoTratamiento",
                                "tipo"
                            ),
                        ]);
                    } else {
                        console.warn(
                            "No se pudieron cargar los tipos de tratamiento:",
                            tiposTratamientoResult.reason
                        );
                    }

                    if (vehiculosResult.status === "fulfilled") {
                        setVehiculos([
                            ...mapList(
                                vehiculosResult.value,
                                "vehiculo",
                                "codVehiculo"
                            ),
                        ]);
                    } else {
                        console.warn(
                            "No se pudieron cargar los tipos de vehículos:",
                            vehiculosResult.reason
                        );
                    }

                    if (combustiblesResult.status === "fulfilled") {
                        setCombustibles([
                            ...mapList(
                                combustiblesResult.value,
                                "combustible",
                                "codCombustible",
                                "codVehiculo",
                                "codsVehiculo"
                            ),
                        ]);
                    } else {
                        console.warn(
                            "No se pudieron cargar los tipos de combustible:",
                            combustiblesResult.reason
                        );
                    }

                    if (imagenesResult.status === "fulfilled") {
                        setImagenes(imagenesResult.value);
                    } else {
                        console.warn(
                            "No se pudieron cargar las imágenes:",
                            imagenesResult.reason
                        );
                    }

                    if (tarifasResult.status === "fulfilled") {
                        setTarifas(tarifasResult.value);
                    } else {
                        console.warn(
                            "No se pudieron cargar las imágenes:",
                            tarifasResult.reason
                        );
                    }

                    if (tiposDocumentosResult.status === "fulfilled") {
                        const tiposDocObligatorios =
                            tiposDocumentosResult.value.filter(
                                (doc) => doc.obligatorio
                            );
                        const tiposDocNoObligatorios =
                            tiposDocumentosResult.value.filter(
                                (doc) => !doc.obligatorio
                            );
                        setTiposDocumentosObligatorios([
                            ...mapList(
                                tiposDocObligatorios,
                                "descripcion",
                                "codTipoDocumento"
                            ),
                        ]);
                        setTiposDocumentosNoObligatorios([
                            ...mapList(
                                tiposDocNoObligatorios,
                                "descripcion",
                                "codTipoDocumento"
                            ),
                        ]);
                        console.log(tiposDocObligatorios);
                        console.log(tiposDocNoObligatorios);
                    } else {
                        console.warn(
                            "No se pudieron cargar los tipos documento:",
                            tiposDocumentosResult.reason
                        );
                    }
                }
            } catch (err) {
                if (isMounted) {
                    setError(
                        "No se han podido cargar los datos principales. Vuelve a intentarlo más tarde."
                    );
                }
                console.error("Error en datos críticos de loadAll:", err);
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        loadAll();

        return () => {
            isMounted = false;
        };
    }, [id, selectedZone, refreshKey, moneda]);

    const fetchCentroProductor = async (id) => {
        try {
            const api = getApiInstance();
            /* La moneda del selector de la cabecera: el backend devuelve los costes
               ya convertidos a ella, con el simbolo aplicado. */
            const response = await api.get(
                `/api/Obra/GetObra/${id}/es`,
                { params: moneda ? { moneda } : undefined }
            );
            return await response.data;
        } catch (error) {
            console.error("Error fetching gestor:", error);
            throw error;
        }
    };

    const fetchDocumentosCentroProductor = async (id) => {
        try {
            const api = getApiInstance();
            const response = await api.get(
                `/api/ObraDocumento/GetDocumentosFromObra/${id}`
            );
            return await response.data;
        } catch (error) {
            console.error("Error fetching gestor:", error);
            throw error;
        }
    };

    const fetchConsumo = async (id) => {
        try {
            const api = getApiInstance();
            const response = await api.get(
                `/api/Consumo/GetConsumoByCodObra/${id}`
            );
            return await response.data;
        } catch (error) {
            console.error("Error fetching gestor:", error);
            throw error;
        }
    };

    const fetchElectricas = async () => {
        try {
            const api = getApiInstance();
            const response = await api.get(`/api/Electrica/GetAllElectricas`);
            return await response.data;
        } catch (error) {
            console.error("Error fetching gestor:", error);
            throw error;
        }
    };

    const fetchResiduos = async () => {
        const api = getApiInstance();
        try {
            const response = await api.get(
                `/api/Residuos/GetAllResiduos`
            );
            console.log("Residuos fetched:", response.data);
            return await response.data;
        } catch (err) {
            console.error(err);
            throw error;
        }
    };

    const fetchProyectos = async () => {
        try {
            const api = getApiInstance();
            const response = await api.get(`/api/Proyecto/GetProyectos`);
            return await response.data;
        } catch (error) {
            console.error("Error fetching proyectos:", error);
            throw error;
        }
    };

    const fetchProveedores = async () => {
        try {
            const api = getApiInstance();
            const response = await api.get(
                `/api/Proveedores/GetProveedores`
            );
            return await response.data;
        } catch (error) {
            console.error("Error fetching proyectos:", error);
            throw error;
        }
    };

    const fetchResiduosTiposTratamiento = async () => {
        try {
            const api = getApiInstance();
            const response = await api.get(
                `/api/ResiduosTipoTratamiento/GetResiduosTipoTratamiento/es`
            );
            return await response.data;
        } catch (error) {
            console.error("Error fetching tipos tratamiento:", error);
            throw error;
        }
    };

    const fetchTiposVehiculos = async () => {
        try {
            const api = getApiInstance();
            const response = await api.get(`/api/Huella/GetTiposVehiculo`);
            return await response.data;
        } catch (error) {
            console.error("Error fetching tipos vehiculos:", error);
            throw error;
        }
    };

    const fetchTiposCombustible = async () => {
        try {
            const api = getApiInstance();
            const response = await api.get(`/api/Huella/GetTiposCombustible`);
            return await response.data;
        } catch (error) {
            console.error("Error fetching tipos combustible:", error);
            throw error;
        }
    };

    const fetchImagenes = async (id) => {
        try {
            const api = getApiInstance();
            const response = await api.get(`/api/Obra/GetImagenesObra/${id}`);

            return await response.data;
        } catch (error) {
            console.error("Error fetching imágenes:", error);
            throw error;
        }
    };

    const fetchTarifas = async (id) => {
        try {
            const api = getApiInstance();
            const response = await api.get(
                `/api/Tarifa/GetTarifasAsignadasByObra/${id}`
            );

            console.log("Tarifas fetched:", response.data);

            return await response.data;
        } catch (error) {
            console.error("Error fetching imágenes:", error);
            throw error;
        }
    };

    const fetchTarifasProveedores = async (id) => {
        try {
            const api = getApiInstance();
            const response = await api.get(
                `/api/Tarifa/GetTarifasAsignadasByObra/${id}`
            );

            console.log("Tarifas fetched:", response.data);

            return await response.data;
        } catch (error) {
            console.error("Error fetching imágenes:", error);
            throw error;
        }
    };

    const fetchTiposDocumentos = async () => {
        try {
            // Productor = 1,
            // Gestor = 2,
            // Obra = 3,
            const api = getApiInstance();
            const response = await api.get(
                `/api/Documentos/GetTiposDocumentosByTipoEntidad/3`
            );

            console.log("Tipos Documentos fetched:", response.data);

            return await response.data;
        } catch (error) {
            console.error("Error fetching tipos documentos:", error);
            throw error;
        }
    };

    const handleTarifaChange = (id, nuevaTarifa) => {
        setTarifas((prevTarifas) =>
            prevTarifas.map((tarifa) =>
                tarifa.id === id ? { ...tarifa, tarifa: nuevaTarifa } : tarifa
            )
        );
    };

    const handleDropdownOpenChange = (rowId, isOpen) => {
        setOpenDropdownRowId(isOpen ? rowId : null);
    };

    const tabsDatos = [
        { id: "informacion", label: "Información General" },
        { id: "ubicacion", label: "Ubicación" },
    ];

    const tabsResiduos = [
        { id: "cantidades", label: "Cantidades generadas" },
        { id: "agrupados", label: "Residuos generados agrupados" },
        { id: "generados", label: "Residuos generados" },
        { id: "tarifas", label: "Tarifas" },
    ];

    const tabsDocumentacion = [
        { id: "documentacion", label: "Documentación" },
        { id: "cumplimiento", label: "Cumplimiento legal" },
        { id: "imagenes", label: "Imágenes" },
    ];

    const renderInformacionGeneral = () => (
        <div className="ficha-centro__info">
            <div className="ficha-centro__field">
                <label>Código Espacio:</label>
                <span>{centro.codEspacio || "'"}</span>
            </div>
            <div className="ficha-centro__field">
                <label>Código Obra:</label>
                <span>{centro.nombre || "-"}</span>
            </div>
            <div className="ficha-centro__field">
                <label>Tipo Obra:</label>
                <span>{centro.tipoObra || "-"}</span>
            </div>
            <div className="ficha-centro__field">
                <label>NIMA:</label>
                <span>{centro.nima || "-"}</span>
            </div>
            <div className="ficha-centro__field">
                <label>Cadena:</label>
                <span>{centro.cadena || "-"}</span>
            </div>
            <div className="ficha-centro__field">
                <label>Fecha inicio:</label>
                <span>{formatDate(centro.fechaInicio) || "-"}</span>
            </div>
            <div className="ficha-centro__field">
                <label>Fecha fin:</label>
                <span>{formatDate(centro.fechaFin) || "-"}</span>
            </div>
            <div className="ficha-centro__field">
                <label>Intermediario:</label>
                <span>{centro.intermediario || "-"}</span>
            </div>
            <div className="ficha-centro__field">
                <label>Responsable:</label>
                <span>{centro.responsable || "-"}</span>
            </div>
            <div className="ficha-centro__field">
                <label>Superficie:</label>
                <span>{centro.superficie || "-"}</span>
            </div>
            <div className="ficha-centro__field">
                <label>Observaciones:</label>
                <span>{centro.observaciones || "-"}</span>
            </div>
        </div>
    );

    const renderUbicacion = () => (
        <div className="ficha-centro__info">
            <div className="ficha-centro__field">
                <label>Ubicación:</label>
                <span>{centro.ubicacion || "-"}</span>
            </div>
            <div className="ficha-centro__field">
                <label>Dirección:</label>
                <span>{centro.direccion || "-"}</span>
            </div>
            <div className="ficha-centro__field">
                <label>Localidad:</label>
                <span>{centro.localidad || "-"}</span>
            </div>
            <div className="ficha-centro__field">
                <label>Provincia:</label>
                <span>{centro.provincia?.provincia || "-"}</span>
            </div>
            <div className="ficha-centro__field">
                <label>Comunidad:</label>
                <span>{centro.comunidad || "-"}</span>
            </div>
            <div className="ficha-centro__field">
                <label>País:</label>
                <span>{centro.pais || "-"}</span>
            </div>
        </div>
    );

    const renderDatosContent = () => {
        switch (activeTabDatos) {
            case "informacion":
                return renderInformacionGeneral();
            case "ubicacion":
                return renderUbicacion();
            default:
                return renderInformacionGeneral();
        }
    };

    const renderCantidadesGeneradas = () => (
        <div className="cantidades-generadas">
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "24px",
                    alignItems: "start",
                }}
            >
                <ScrollableBarChartGraph
                    data={datosProcesadosBar}
                    passItemToFormatter
                    valueFormatter={(value, item) =>
                        formatPesoPorTipo(value, Boolean(item?.peligroso))
                    }
                />

                <PieChartGraph
                    data={{ all: datosProcesadosPie }}
                    activeTab="all"
                    tooltipAsPercent={false}
                    tooltipValueFormatter={(value) =>
                        `${formatPeso(value)}`
                    }
                    legendAsPercent
                    centerLabel={`${Number(porcentajeValorizacion).toFixed(
                        1
                    )}%`}
                    centerSubLabel="Valorización"
                />
            </div>
        </div>
    );

    const renderImagenes = () => {
        const handleEliminarImagen = async (imagenId, fileName, codObra) => {
            console.log("Eliminar imagen:", imagenId, fileName, codObra);

            if (
                window.confirm(
                    "¿Estás seguro de que deseas eliminar esta imagen?"
                )
            ) {
                try {
                    const api = getApiInstance();
                    await api.delete(
                        `/api/Obra/DeleteImagen/${imagenId}/${codObra}/${fileName}`
                    );
                    setImagenes(
                        imagenes.filter((img) => img.codImagen !== imagenId)
                    );
                } catch (error) {
                    console.error("Error eliminando imagen:", error);
                    alert("Error al eliminar la imagen. Inténtalo de nuevo.");
                }
            }
        };

        const handleSubirImagenes = async (e) => {
            const files = e.target.files;
            if (!files || files.length === 0) return;

            try {
                const api = getApiInstance();
                const formData = new FormData();

                for (const file of files) {
                    formData.append("Imagenes", file);
                }

                const { data } = await api.post(
                    `/api/Obra/SetImage/${id}`,
                    formData
                );

                const imagenesActualizadas = await fetchImagenes(id);
                setImagenes(imagenesActualizadas);
            } catch (error) {
                console.error("Error subiendo imágenes:", error);
                alert("Error al subir las imágenes. Inténtalo de nuevo.");
            } finally {
                // Resetea el input para poder volver a seleccionar el mismo archivo si se desea
                e.target.value = "";
            }
        };

        const formatearFecha = (fechaString) => {
            const fecha = new Date(fechaString);
            const dia = String(fecha.getDate()).padStart(2, "0");
            const mes = String(fecha.getMonth() + 1).padStart(2, "0");
            const año = fecha.getFullYear();
            const horas = String(fecha.getHours()).padStart(2, "0");
            const minutos = String(fecha.getMinutes()).padStart(2, "0");
            const segundos = String(fecha.getSeconds()).padStart(2, "0");
            return `${dia}/${mes}/${año} ${horas}:${minutos}:${segundos}`;
        };

        if (!imagenes || imagenes.length === 0) {
            return (
                <div className="imagenes-section">
                    <div className="imagenes-upload-area">
                        <div className="imagenes-upload-content">
                            <p>Haz click aquí para subir imágenes</p>
                            <input
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={handleSubirImagenes}
                                style={{ display: "none" }}
                                id="upload-imagenes"
                            />
                            <label
                                htmlFor="upload-imagenes"
                                className="upload-label"
                            >
                                Seleccionar archivos
                            </label>
                        </div>
                    </div>
                    <div className="imagenes-empty">
                        <p>Actualmente no hay imágenes para mostrar</p>
                    </div>
                </div>
            );
        }

        return (
            <div className="imagenes-section">
                <div className="imagenes-upload-area">
                    <div className="imagenes-upload-content">
                        <p>Haz click aquí para subir imágenes</p>
                        <input
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handleSubirImagenes}
                            style={{ display: "none" }}
                            id="upload-imagenes"
                        />
                        <label
                            htmlFor="upload-imagenes"
                            className="upload-label"
                        >
                            Seleccionar archivos
                        </label>
                    </div>
                </div>
                <div className="imagenes-grid">
                    {imagenes.map((imagen) => (
                        <div key={imagen.codImagen} className="imagen-card">
                            <div className="imagen-thumbnail">
                                {/* GetArchivoBase64 devuelve "" cuando no encuentra
                                    el fichero en wwwroot y se traga la excepción, así
                                    que la fila existe en base pero la imagen no. Sin
                                    esto se pintaba un <img> roto con el texto alt. */}
                                {imagen.fileBase64 ? (
                                    <img
                                        src={`data:image/png;base64,${imagen.fileBase64}`}
                                        alt={`Imagen ${imagen.codImagen}`}
                                    />
                                ) : (
                                    <div className="imagen-thumbnail__vacia">
                                        <IoImageOutline />
                                        <span>Imagen no disponible</span>
                                    </div>
                                )}
                            </div>
                            <div className="imagen-info">
                                <div className="imagen-detalles">
                                    <p className="imagen-usuario">
                                        {imagen.usuario || "Usuario"}
                                    </p>
                                    <p className="imagen-fecha">
                                        {formatearFecha(imagen.fechaSubida)}
                                    </p>
                                </div>
                                <button
                                    className="eliminar-imagen-btn"
                                    onClick={() =>
                                        handleEliminarImagen(
                                            imagen.codImagen,
                                            imagen.fileName,
                                            id
                                        )
                                    }
                                    aria-label="Eliminar imagen"
                                    title="Eliminar imagen"
                                >
                                    <IoTrashOutline />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const renderDocumentacionContent = () => {
        switch (activeTabDocumentacion) {
            case "documentacion":
                return (
                    <div className="ficha-centro__documentacion">
                        <Table
                            columns={[
                                {
                                    key: "tipoDocumento",
                                    label: "Tipo de documento",
                                    visible: true,
                                    sortable: true,
                                },
                                {
                                    key: "documento",
                                    label: "Documento",
                                    visible: true,
                                    sortable: true,
                                    documentProp: "ficheroBase64",
                                    type: "document",
                                },
                                {
                                    key: "codEstado",
                                    label: "Validación",
                                    visible: true,
                                    sortable: true,
                                    titleProp: "lastValidationNote",
                                },
                            ]}
                            dataTable={documentos}
                            noResultsMessage="No hay documentos disponibles"
                            rowsPerPage={10}
                            showCheckboxes={false}
                            showAdvancedFilters={false}
                            /* Mismo gating que la app antigua para el boton de
                               nuevo documento: escritura y centro no cerrado. */
                            hideAddButton={!puedeEditarResiduos}
                            codTipoValidacion={6}
                            actions={[
                                ...(puedeValidar
                                    ? [
                                          {
                                              label: "Validar",
                                              actionType: "validate",
                                              icon: <CiCircleCheck size={16} />,
                                          },
                                      ]
                                    : []),
                                ...(puedeVerValidaciones
                                    ? [
                                          {
                                              label: "Ver validaciones",
                                              action: abrirHistoricoValidaciones(
                                                  6
                                              ),
                                              icon: <CiClock1 size={16} />,
                                          },
                                      ]
                                    : []),
                                ...(puedeEditarResiduos ? ["Eliminar"] : []),
                            ]}
                            elementName="Documento"
                            keyProp="codDocumento"
                            searchTerms={["tipoDocumento", "documento"]}
                            stateProp="codEstado"
                            states={[
                                {
                                    label: "Sin datos",
                                    type: "info",
                                    Icon: CiClock1,
                                },
                                {
                                    label: "Pendiente de validar",
                                    type: "info",
                                    Icon: CiClock1,
                                },
                                {
                                    label: "Validación con incidencias",
                                    type: "danger",
                                    Icon: CgDanger,
                                },
                                {
                                    label: "Validada",
                                    type: "success",
                                    Icon: CiCircleCheck,
                                },
                                {
                                    label: "Expirado",
                                    type: "danger",
                                    Icon: CiCircleCheck,
                                },
                            ]}
                            stateChangeOptions={[
                                { value: "1", label: "Pendiente de validar" },
                                {
                                    value: "2",
                                    label: "Validación con incidencias",
                                },
                                { value: "3", label: "Validada" },
                                { value: "4", label: "Expirado" },
                            ]}
                            /* Sin permiso para validar no se pasa la URL: así la tabla no
                               ofrece el cambio de estado ni el botón de validar en bloque. */
                            stateChangeUrl={
                                puedeValidar
                                    ? "/api/Validaciones/SetValidacion"
                                    : undefined
                            }
                            formElements={formElementsDocumentos}
                            addApiUrl="/api/ObraDocumento/SetDocumento"
                            deleteApiUrl="/api/ObraDocumento/DeleteDocumento"
                            onDataUpdate={(newData) => setDocumentos(newData)}
                            userCodAcceso={Number(user.codAcceso)}
                            userCodPerfil={Number(user.role)}
                            loading={loading}
                        />
                    </div>
                );
            case "cumplimiento":
                return (
                    <div className="ficha-centro__cumplimiento">
                        <Table
                            columns={[
                                {
                                    key: "nombre",
                                    label: "Descripción",
                                    visible: true,
                                    sortable: true,
                                },
                                {
                                    key: "codEstado",
                                    label: "Estado",
                                    visible: true,
                                    sortable: true,
                                },
                            ]}
                            dataTable={centro.cumplimientos}
                            noResultsMessage="No hay datos de cumplimiento legal disponibles"
                            rowsPerPage={10}
                            showCheckboxes={false}
                            showAdvancedFilters={false}
                            elementName="Cumplimiento"
                            searchTerms={["nombre"]}
                            states={[
                                {
                                    label: "Sin datos",
                                    type: "info",
                                    Icon: CiClock1,
                                },
                                {
                                    label: "Pendiente",
                                    type: "info",
                                    Icon: CiClock1,
                                },
                                {
                                    label: "Sin validar",
                                    type: "warning",
                                    Icon: CiClock1,
                                },
                                {
                                    label: "Validado",
                                    type: "success",
                                    Icon: CiClock1,
                                },
                            ]}
                            stateProp="codEstado"
                            sortProp="descripcion"
                            userCodAcceso={Number(user.codAcceso)}
                            userCodPerfil={Number(user.role)}
                            loading={loading}
                        />
                    </div>
                );
            case "imagenes":
                return renderImagenes();
            default:
                return (
                    <div className="ficha-centro__documentacion">
                        <Table
                            columns={[
                                {
                                    key: "tipoDocumento",
                                    label: "Tipo de documento",
                                    visible: true,
                                    sortable: true,
                                },
                                {
                                    key: "documento",
                                    label: "Documento",
                                    visible: true,
                                    sortable: true,
                                    documentProp: "ficheroBase64",
                                    type: "document",
                                },
                                {
                                    key: "codEstado",
                                    label: "Validación",
                                    visible: true,
                                    sortable: true,
                                },
                            ]}
                            dataTable={documentos}
                            noResultsMessage="No hay documentos disponibles"
                            rowsPerPage={10}
                            showCheckboxes={false}
                            actions={["Editar", "Eliminar"]}
                            elementName="Documento"
                            searchTerms={["tipoDocumento", "documento"]}
                            stateProp="codEstado"
                            states={[
                                {
                                    label: "Sin datos",
                                    type: "info",
                                    Icon: CiClock1,
                                },
                                {
                                    label: "Pendiente de validar",
                                    type: "info",
                                    Icon: CiClock1,
                                },
                                {
                                    label: "Validación con incidencias",
                                    type: "danger",
                                    Icon: CgDanger,
                                },
                                {
                                    label: "Validada",
                                    type: "success",
                                    Icon: CiCircleCheck,
                                },
                            ]}
                            formElements={formElementsDocumentos}
                            addApiUrl="/api/gestores/documentos"
                            editApiUrl="/api/gestores/documentos"
                            deleteApiUrl="/api/gestores/documentos"
                            userCodAcceso={Number(user.codAcceso)}
                            userCodPerfil={Number(user.role)}
                            loading={false}
                        />
                    </div>
                );
        }
    };

    const renderConsumo = () => (
        <Table
            columns={[
                {
                    key: "electrica",
                    label: "Eléctrica",
                    visible: true,
                    sortable: true,
                },
                {
                    key: "fechaInicio",
                    label: "Fecha inicio",
                    visible: true,
                    sortable: true,
                    type: "date",
                },
                {
                    key: "fechaFin",
                    label: "Fecha fin",
                    visible: true,
                    sortable: true,
                    type: "date",
                },
                {
                    key: "consumo",
                    label: "Consumo",
                    visible: true,
                    sortable: true,
                },
                {
                    key: "factura",
                    label: "Factura",
                    visible: true,
                    sortable: true,
                    type: "document",
                    documentProp: "ficheroBase64",
                },
            ]}
            dataTable={consumo}
            noResultsMessage="No hay datos de consumo disponibles"
            rowsPerPage={10}
            showCheckboxes={false}
            elementName="Consumo"
            sortProp="electrica"
            keyProp="codConsumo"
            searchTerms={["electrica", "fechaInicio", "fechaFin", "consumo"]}
            showAdvancedFilters={false}
            actions={["Eliminar", "Editar"]}
            loading={loading}
            formElements={[
                {
                    elements: [
                        {
                            name: "codObra",
                            type: "text",
                            required: false,
                            disabled: false,
                            visible: false,
                            defaultValue: `${id}`,
                        },
                        {
                            name: "codElectrica",
                            type: "select",
                            label: "Distribuidora",
                            placeholder: "Seleccione una distribuidora",
                            options: distribuidoras,
                            required: true,
                            disabled: false,
                            labelVisible: true,
                            visible: true,
                        },
                        {
                            name: "codGarantia",
                            type: "select",
                            label: "Garantía de Origen (GDO)",
                            placeholder:
                                "¿Dispone de Garantía de Origen (GDO)?",
                            required: true,
                            disabled: false,
                            labelVisible: true,
                            visible: true,
                            options: [
                                { value: "1", label: "GdO energía renovable" },
                                {
                                    value: "2",
                                    label: "GdO cogeneración de alta eficiencia",
                                },
                                { value: "3", label: "No" },
                            ],
                        },
                        {
                            name: "fechaInicio",
                            type: "date",
                            label: "Fecha Inicio",
                            required: true,
                            disabled: false,
                            labelVisible: true,
                            visible: true,
                        },
                        {
                            name: "fechaFin",
                            type: "date",
                            label: "Fecha Fin",
                            required: true,
                            disabled: false,
                            labelVisible: true,
                            visible: true,
                        },
                        {
                            name: "consumo",
                            type: "number",
                            label: "Consumo (kWh)",
                            placeholder: "Ingrese el consumo en kWh",
                            required: true,
                            disabled: false,
                            labelVisible: true,
                            visible: true,
                        },
                        {
                            name: "FicheroFactura",
                            type: "document",
                            label: "Factura",
                            placeholder: "Subir factura",
                            required: true,
                            disabled: false,
                            labelVisible: true,
                            visible: true,
                        },
                    ],
                },
            ]}
            addApiUrl="/api/Consumo/SetConsumo"
            editApiUrl="/api/Consumo/EditConsumo"
            deleteApiUrl="/api/Consumo/DeleteConsumo"
            userCodAcceso={Number(user.codAcceso)}
            userCodPerfil={Number(user.role)}
            hideAddButton
            ref={consumoTableRef}
        />
    );

    async function handleBitacora() {
        const api = getApiInstance();
        const response = await api.post(
            `api/Obra/obtener-bitacora-mabe?codObra=${id}`,
            null,

            {
                responseType: "blob",
            }
        );

        if (response.status !== 200) {
            throw new Error("Error al generar la bitácora.");
        }
        const cd = response.headers["content-disposition"] || "";
        let fileName = `Informe_${id}.xlsx`;

        const utf8Match = cd.match(/filename\*=UTF-8''([^;]+)/i);

        const normalMatch = cd.match(/filename="?([^"]+)"?/i);

        const rawFileName = utf8Match?.[1] || normalMatch?.[1];

        if (rawFileName) {
            try {
                fileName = decodeURIComponent(rawFileName.trim());
            } catch {
                fileName = rawFileName.trim();
            }
        }
        // Blob
        const blob =
            response.data instanceof Blob
                ? response.data
                : new Blob([response.data], {
                      type:
                          response.headers["content-type"] ||
                          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                  });

        const urlBlob = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = urlBlob;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(urlBlob);

        return { ok: true, fileName, size: blob.size };
    }

    async function handleBitacoraResiduo(row) {
        const api = getApiInstance();

        console.log("Generando bitácora para residuo:", row);

        const response = await api.get("api/ResiduosObra/bitacora-residuos", {
            params: {
                codObra: id,
                codResiduo:
                    selectedBitacoraRow.codResiduo ??
                    selectedBitacoraRow.CodResiduo ??
                    selectedBitacoraRow.id,
                mes: selectedMonthBitacora.value,
                codZona: selectedZone.value,
            },
            responseType: "blob",
        });

        if (response.status !== 200) {
            setIsModalBitacoraOpen(false);
            setSelectedMonthBitacora(null);
            notificationRef.current?.addNotification(
                "Error al generar la bitácora. Inténtalo de nuevo más tarde.",
                "error"
            );
        }

        const cd = response.headers["content-disposition"] || "";
        let fileName = `Manifiesto_MABE_${id}.docx`;

        const utf8Match = cd.match(/filename\*=UTF-8''([^;]+)/i);
        const normalMatch = cd.match(/filename="?([^"]+)"?/i);

        const rawFileName = utf8Match?.[1] || normalMatch?.[1];

        if (rawFileName) {
            try {
                fileName = decodeURIComponent(rawFileName.trim());
            } catch {
                fileName = rawFileName.trim();
            }
        }

        const blob =
            response.data instanceof Blob
                ? response.data
                : new Blob([response.data], {
                      type:
                          response.headers["content-type"] ||
                          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                  });

        const urlBlob = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = urlBlob;
        link.download = fileName;

        document.body.appendChild(link);
        link.click();

        link.remove();
        window.URL.revokeObjectURL(urlBlob);

        setIsModalBitacoraOpen(false);
        setSelectedMonthBitacora(null);
        notificationRef.current?.addNotification(
            "Informe generado correctamente",
            "success"
        );

        return {
            ok: true,
            fileName,
            size: blob.size,
        };
    }

    const handleExportarObra = async () => {
        if (exportando) return;
        setExportando(true);
        const api = getApiInstance();

        try {
            /* El informe de la obra (el de la app antigua), no el listado de
               centros. Con la moneda del selector, como los costes de la ficha. */
            const response = await api.post(
                `/api/Obra/CreateReportExcel/${id}`,
                null,
                {
                    responseType: "blob",
                    params: moneda ? { moneda } : undefined,
                }
            );

            const cd = response.headers["content-disposition"] || "";
            let fileName = `Informe_CentroProductor_${id}.xlsx`;

            const utf8Match = cd.match(/filename\*=UTF-8''([^;]+)/i);
            const normalMatch = cd.match(/filename="?([^"]+)"?/i);
            const rawFileName = utf8Match?.[1] || normalMatch?.[1];

            if (rawFileName) {
                try {
                    fileName = decodeURIComponent(rawFileName.trim());
                } catch {
                    fileName = rawFileName.trim();
                }
            }

            const blob =
                response.data instanceof Blob
                    ? response.data
                    : new Blob([response.data], {
                          type:
                              response.headers["content-type"] ||
                              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                      });

            const urlBlob = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = urlBlob;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(urlBlob);

            notificationRef.current?.addNotification(
                "Informe generado correctamente",
                "success"
            );
        } catch (err) {
            console.error(err);
            notificationRef.current?.addNotification(
                "Error al generar el informe. Inténtalo de nuevo más tarde.",
                "error"
            );
        } finally {
            setExportando(false);
        }
    };

    const ejecutarAccionObra = async (endpoint, mensaje, exito) => {
        if (!centro) return;
        if (!window.confirm(mensaje)) return;

        try {
            const api = getApiInstance();
            await api.post(`${endpoint}/${id}`);
            notificationRef.current?.addNotification(exito, "success");
            setRefreshKey((k) => k + 1);
        } catch (err) {
            console.error(err);
            notificationRef.current?.addNotification(
                "No se pudo completar la acción. Inténtalo de nuevo.",
                "error"
            );
        }
    };

    const handleAbrirCentro = () => {
        ejecutarAccionObra(
            "/api/Obra/OpenObra",
            `¿Seguro que quieres abrir de nuevo el centro ${centro?.codEspacio}?`,
            "Centro abierto correctamente"
        );
    };

    const handleCerrarCentro = () => {
        const esIntermediario = Number(user.role) === 4;
        ejecutarAccionObra(
            esIntermediario
                ? "/api/Obra/RequestCloseObra"
                : "/api/Obra/CloseObra",
            esIntermediario
                ? `¿Seguro que quieres solicitar el cierre del centro ${centro?.codEspacio}?`
                : `¿Seguro que quieres cerrar el centro ${centro?.codEspacio}?`,
            esIntermediario
                ? "Solicitud de cierre enviada correctamente"
                : "Centro cerrado correctamente"
        );
    };

    const handleIncorrecto = () => {
        ejecutarAccionObra(
            "/api/Obra/MarkIncorrectObra",
            `¿Marcar el centro ${centro?.codEspacio} como incorrecto?`,
            "Centro marcado como incorrecto"
        );
    };

    const handleQuitarIncorrecto = () => {
        ejecutarAccionObra(
            "/api/Obra/ClearIncorrectObra",
            `¿Quitar la marca de incorrecto del centro ${centro?.codEspacio}?`,
            "Marca de incorrecto retirada"
        );
    };

    const handleDescargarPlantilla = async () => {
        if (exportando) return;
        setExportando(true);
        const api = getApiInstance();

        try {
            const response = await api.get(
                `/api/ObraDocumento/GetPlantillaObra`,
                { responseType: "blob" }
            );

            if (response.status !== 200) {
                throw new Error("Error al descargar la plantilla.");
            }

            const cd = response.headers["content-disposition"] || "";
            let fileName = `Plantilla_Obra_${id}.xlsx`;

            const utf8Match = cd.match(/filename\*=UTF-8''([^;]+)/i);
            const normalMatch = cd.match(/filename="?([^"]+)"?/i);
            const rawFileName = utf8Match?.[1] || normalMatch?.[1];

            if (rawFileName) {
                try {
                    fileName = decodeURIComponent(rawFileName.trim());
                } catch {
                    fileName = rawFileName.trim();
                }
            }

            const blob =
                response.data instanceof Blob
                    ? response.data
                    : new Blob([response.data], {
                          type:
                              response.headers["content-type"] ||
                              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                      });

            const urlBlob = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = urlBlob;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(urlBlob);

            notificationRef.current?.addNotification(
                "Plantilla descargada correctamente",
                "success"
            );
        } catch (err) {
            console.error(err);
            notificationRef.current?.addNotification(
                "Error al descargar la plantilla. Inténtalo de nuevo más tarde.",
                "error"
            );
        } finally {
            setExportando(false);
        }
    };

    const handleEditarCentro = () => {
        if (!centro) return;
        setFormEdicionCentro({
            codEspacio: centro.codEspacio || "",
            nombre: centro.nombre || "",
            direccion: centro.direccion || "",
            localidad: centro.localidad || "",
            codIntegracion: centro.codIntegracion || "",
            promotor: centro.promotor || "",
            observaciones: centro.observaciones || "",
            responsable: centro.responsable || "",
            mailResponsable: centro.mailResponsable || "",
        });
        setModalEdicionAbierto(true);
    };

    const handleCambioEdicionCentro = (campo, valor) => {
        setFormEdicionCentro((prev) => ({ ...prev, [campo]: valor }));
    };

    const handleGuardarEdicionCentro = async () => {
        if (!centro) return;
        try {
            const api = getApiInstance();
            const payload = {
                ...centro,
                ...formEdicionCentro,
            };
            delete payload.ProductorObj;
            delete payload.residuos;
            await api.put(`/api/Obra/UpdateObra`, payload);
            notificationRef.current?.addNotification(
                "Centro actualizado correctamente",
                "success"
            );
            setModalEdicionAbierto(false);
            setRefreshKey((k) => k + 1);
        } catch (err) {
            console.error(err);
            notificationRef.current?.addNotification(
                "Error al actualizar el centro. Inténtalo de nuevo.",
                "error"
            );
        }
    };

    const renderHeaderActions = () => {
        if (!centro) return null;

        const estado = Number(centro.codEstado);
        const esCerrado = estado === 2;
        const esIncorrecto = estado === 7;
        const esSinDatos = estado === 0 || estado === 1;
        const esSolicitudCierre = estado === 6;
        const esAdmin = [1, 2].includes(Number(user.role));
        const esIntermediario = Number(user.role) === 4;
        const puedeCerrarDirecto = puedeGestionarCentro && !esIntermediario;
        const puedeSolicitarCierre =
            puedeGestionarCentro && esIntermediario;
        /* El legacy oculta "Exportar Obra" de la ficha al Intermediario
           (Details.cshtml:100); desde el listado sí puede exportar. */
        const puedeExportarFicha = puedeGestionarCentro && !esIntermediario;

        const acciones = [];

        if (esCerrado) {
            if (puedeExportarFicha) {
                acciones.push(
                    <Button
                        key="exportar"
                        variant="outlined"
                        startIcon={<FiDownload />}
                        onClick={handleExportarObra}
                        disabled={exportando}
                    >
                        {exportando ? "Generando..." : "Exportar Obra"}
                    </Button>
                );
            }
            if (puedeGestionarCentro && esAdmin) {
                acciones.push(
                    <Button
                        key="abrir"
                        variant="filled"
                        startIcon={<IoLockOpenOutline />}
                        onClick={handleAbrirCentro}
                    >
                        Abrir Centro
                    </Button>
                );
            }
        } else if (esIncorrecto) {
            if (puedeCerrarDirecto) {
                acciones.push(
                    <Button
                        key="quitar"
                        variant="outlined"
                        startIcon={<IoReloadOutline />}
                        onClick={handleQuitarIncorrecto}
                    >
                        Quitar incorrecto
                    </Button>
                );
            }
            if (puedeExportarFicha) {
                acciones.push(
                    <Button
                        key="exportar"
                        variant="outlined"
                        startIcon={<FiDownload />}
                        onClick={handleExportarObra}
                        disabled={exportando}
                    >
                        {exportando ? "Generando..." : "Exportar Obra"}
                    </Button>
                );
            }
        } else {
            if (puedeCerrarDirecto) {
                acciones.push(
                    <Button
                        key="plantilla"
                        variant="outlined"
                        startIcon={<FiDownload />}
                        onClick={handleDescargarPlantilla}
                    >
                        Descargar plantilla
                    </Button>,
                    <Button
                        key="cerrar"
                        variant="outlined"
                        startIcon={<IoStopOutline />}
                        onClick={handleCerrarCentro}
                    >
                        Cerrar centro
                    </Button>,
                    <Button
                        key="editar"
                        variant="outlined"
                        startIcon={<FiEdit />}
                        onClick={handleEditarCentro}
                    >
                        Editar centro
                    </Button>,
                    <Button
                        key="incorrecto"
                        variant="outlined"
                        className="ficha-centro__btn--danger"
                        startIcon={<IoClose />}
                        onClick={handleIncorrecto}
                    >
                        Incorrecto
                    </Button>
                );
            }

            if (puedeSolicitarCierre && !esSolicitudCierre) {
                acciones.push(
                    <Button
                        key="solicitarCierre"
                        variant="outlined"
                        startIcon={<IoStopOutline />}
                        onClick={handleCerrarCentro}
                    >
                        Solicitar Cierre
                    </Button>
                );
            }

            if (!esSinDatos && puedeExportarFicha) {
                acciones.push(
                    <Button
                        key="exportar"
                        variant="outlined"
                        startIcon={<FiDownload />}
                        onClick={handleExportarObra}
                        disabled={exportando}
                    >
                        {exportando ? "Generando..." : "Exportar Obra"}
                    </Button>
                );
            }
        }

        return acciones;
    };

    return (
        <DetalleLayout
            title={
                centro
                    ? `Centro ${centro.codEspacio} - ${centro.direccion} -${centro.localidad}`
                    : "Cargando..."
            }
            backLink="/centros-productores"
            backText="Volver a Centros"
            loading={loading}
            error={error}
            notFound={!centro && !loading && !error}
            className="ficha-centro"
            headerActions={renderHeaderActions()}
        >
            {isMabe && (
                <div className="ficha-centro__actions">
                    <Button
                        variant="filled"
                        onClick={handleBitacora}
                        className="ficha-centro__manifiesto-btn"
                    >
                        Obtener bitácora
                    </Button>
                </div>
            )}

            {!loading && !error && centro && (
                <div className="ficha-centro__content">
                    <div className="ficha-centro__section">
                        <div className="ficha-centro__section-header">
                            <h2>Datos del centro</h2>
                        </div>
                        <TabsNavigation
                            tabs={tabsDatos}
                            activeTab={activeTabDatos}
                            onTabChange={setActiveTabDatos}
                        />
                        <div className="ficha-centro__section-content">
                            {renderDatosContent()}
                        </div>
                    </div>
                    <div className="ficha-centro__section">
                        <div className="ficha-centro__section-header">
                            <h2>Residuos</h2>
                        </div>
                        <TabsNavigation
                            tabs={tabsResiduos}
                            activeTab={activeTabResiduos}
                            onTabChange={setActiveTabResiduos}
                        />
                        <div className="ficha-centro__section-content">
                            <div
                                style={{
                                    display:
                                        activeTabResiduos === "cantidades"
                                            ? "block"
                                            : "none",
                                }}
                            >
                                {renderCantidadesGeneradas()}
                            </div>
                            <div
                                style={{
                                    display:
                                        activeTabResiduos === "agrupados"
                                            ? "block"
                                            : "none",
                                }}
                            >
                                {/* TABLE AGRUPADOS */}
                                <Table
                                    columns={[
                                        {
                                            key: "residuo",
                                            label: "Residuo",
                                            visible: true,
                                            sortable: true,
                                        },
                                        {
                                            key: "peso",
                                            label: "Peso",
                                            visible: true,
                                            sortable: true,
                                            render: (row) =>
                                                formatPesoResiduo(
                                                    row.peso,
                                                    row.codTipoResiduo
                                                ),
                                        },
                                        {
                                            key: "valorizacion",
                                            label: "Valorización",
                                            visible: true,
                                            sortable: true,
                                            render: (row) => {
                                                /* "Sin datos" solo si ninguna línea
                                                   trae tratamiento final con %. Todo a
                                                   eliminación es un dato: 0%. El % va
                                                   sobre el peso con dato, como el
                                                   informe y la app antigua. */
                                                if (
                                                    !(
                                                        row.pesoConDatoValorizacion >
                                                        0
                                                    )
                                                ) {
                                                    return (
                                                        <span>Sin datos</span>
                                                    );
                                                }

                                                const porcentajeAgrupado =
                                                    ((row.valorizacion || 0) /
                                                        row.pesoConDatoValorizacion) *
                                                    100;

                                                const peso =
                                                    pesoEnUnidadResiduo(
                                                        row.pesoConDatoValorizacion,
                                                        row.codTipoResiduo
                                                    );
                                                const valorizado =
                                                    pesoEnUnidadResiduo(
                                                        row.valorizacion,
                                                        row.codTipoResiduo
                                                    );

                                                return (
                                                    <div className="valorizacion-container">
                                                        <span htmlFor="valorizacion">
                                                            {valorizado.toFixed(
                                                                3
                                                            )}{" "}
                                                            / {peso}{" "}
                                                            {unidadResiduo(
                                                                row.codTipoResiduo
                                                            )}
                                                        </span>
                                                        <div className="valorizacion-barra">
                                                            <progress
                                                                id="valorizacion"
                                                                value={
                                                                    valorizado
                                                                }
                                                                max={peso}
                                                                className="progress-bar"
                                                                style={obtenerEstiloDeProgreso(
                                                                    valorizado,
                                                                    peso
                                                                )}
                                                            ></progress>
                                                            <span className="valorizacion-porcentaje">
                                                                {porcentajeAgrupado.toFixed(
                                                                    1
                                                                )}
                                                                %
                                                            </span>
                                                        </div>
                                                    </div>
                                                );
                                            },
                                        },
                                        {
                                            key: "costeTotal",
                                            label: "Coste total",
                                            visible: true,
                                            sortable: true,
                                            render: (row) => (
                                                <span
                                                    className={`coste ${
                                                        row.costeTotal < 0
                                                            ? "coste--negative"
                                                            : "coste--positive"
                                                    }`}
                                                >
                                                    {formateaCoste.format(
                                                        row.costeTotal ?? 0
                                                    )}
                                                </span>
                                            ),
                                        },
                                    ]}
                                    dataTable={residuosObraGroupped}
                                    searchTerms={["residuo", "peso"]}
                                    noResultsMessage="No hay residuos agrupados disponibles"
                                    rowsPerPage={10}
                                    keyProp="codResiduo"
                                    sortProp="residuo"
                                    showCheckboxes={false}
                                    elementName="residuo"
                                    stateProp="estado"
                                    userCodAcceso={Number(user.codAcceso)}
                                    userCodPerfil={Number(user.role)}
                                    loading={false}
                                    excludeFields={["residuo", "codTipoResiduo", "codResiduo", "valorizacion", "pesoConDatoValorizacion", "pesoSinDatoValorizacion"]}
                                />
                            </div>
                            <div
                                style={{
                                    display:
                                        activeTabResiduos === "generados"
                                            ? "block"
                                            : "none",
                                }}
                            >
                                <Table
                                    columns={[
                                        {
                                            key: [
                                                "residuo/codigo",
                                                "residuo/nombre",
                                                "observacionesGestionFinal",
                                            ],
                                            label: "Residuo",
                                            visible: true,
                                            sortable: true,
                                        },
                                        {
                                            key: "fechaTransporte",
                                            label: "Fecha recogida",
                                            visible: true,
                                            sortable: true,
                                            type: "date",
                                        },
                                        {
                                            key: "peso",
                                            label: "Peso",
                                            visible: true,
                                            sortable: true,
                                            render: (row) =>
                                                formatPesoResiduo(
                                                    row.peso,
                                                    row.codTipoResiduo
                                                ),
                                        },
                                        {
                                            key: "valorizacion",
                                            label: "Valorización",
                                            visible: true,
                                            sortable: true,
                                            render: (row) => {
                                                /* La valorización y su % los calcula
                                                   el backend según el tratamiento
                                                   final (0 si es de eliminación);
                                                   Porcentaje2 solo no basta. */
                                                if (row.valorizacion == null) {
                                                    return (
                                                        <span>Sin datos</span>
                                                    );
                                                }

                                                /* El backend manda el peso en kg;
                                                   se muestra en la unidad del tipo
                                                   de residuo, como la app antigua. */
                                                const peso =
                                                    pesoEnUnidadResiduo(
                                                        row.peso,
                                                        row.codTipoResiduo
                                                    );
                                                const valorizado =
                                                    pesoEnUnidadResiduo(
                                                        row.valorizacion,
                                                        row.codTipoResiduo
                                                    );

                                                return (
                                                    <div className="valorizacion-container">
                                                        <span htmlFor="valorizacion">
                                                            {valorizado.toFixed(
                                                                3
                                                            )}{" "}
                                                            / {peso}{" "}
                                                            {unidadResiduo(
                                                                row.codTipoResiduo
                                                            )}
                                                        </span>
                                                        <div className="valorizacion-barra">
                                                            <progress
                                                                id="valorizacion"
                                                                value={
                                                                    valorizado
                                                                }
                                                                max={peso}
                                                                className="progress-bar"
                                                                style={obtenerEstiloDeProgreso(
                                                                    valorizado,
                                                                    peso
                                                                )}
                                                            ></progress>
                                                            <span className="valorizacion-porcentaje">
                                                                {(
                                                                    Number(
                                                                        row.porcentajeValorizacion
                                                                    ) || 0
                                                                ).toFixed(1)}
                                                                %
                                                            </span>
                                                        </div>
                                                    </div>
                                                );
                                            },
                                        },
                                        {
                                            key: "costeTotal",
                                            label: "Coste final",
                                            visible: true,
                                            sortable: true,
                                            render: (row) => (
                                                <span
                                                    className={`coste ${
                                                        row.costeTotal < 0
                                                            ? "coste--negative"
                                                            : "coste--positive"
                                                    }`}
                                                >
                                                    {formateaCoste.format(
                                                        row.costeTotal ?? 0
                                                    )}
                                                </span>
                                            ),
                                        },
                                        {
                                            key: "codEstado",
                                            label: "Validación",
                                            visible: true,
                                            sortable: true,
                                            titleProp: "lastValidationNote",
                                        },
                                    ]}
                                    dataTable={residuosGenerados}
                                    noResultsMessage="No hay residuos generados disponibles"
                                    rowsPerPage={10}
                                    showCheckboxes={false}
                                    /* Sobre un centro cerrado o incorrecto no se
                                       añaden residuos. La tabla solo mira el acceso
                                       y el perfil, no el estado del centro. */
                                    hideAddButton={!puedeEditarResiduos}
                                    codTipoValidacion={2}
                                    actions={[
                                        ...(puedeEditarResiduos
                                            ? ["Editar"]
                                            : []),
                                        ...(puedeValidar
                                            ? [
                                                  {
                                                      label: "Validar",
                                                      actionType: "validate",
                                                      icon: (
                                                          <CiCircleCheck
                                                              size={16}
                                                          />
                                                      ),
                                                  },
                                              ]
                                            : []),
                                        ...(puedeVerValidaciones
                                            ? [
                                                  {
                                                      label: "Ver validaciones",
                                                      action: abrirHistoricoValidaciones(
                                                          2
                                                      ),
                                                      icon: (
                                                          <CiClock1 size={16} />
                                                      ),
                                                  },
                                              ]
                                            : []),
                                        /* "Generar manifiesto": el legacy lo
                                           mostraba solo en la zona 2 (México);
                                           aquí el equivalente es el flag isMabe. */
                                        ...(isMabe ? ["Exportar"] : []),
                                        ...(puedeEditarResiduos
                                            ? ["Eliminar"]
                                            : []),
                                    ]}
                                    onDataUpdate={(newData) => {
                                        // Optimista para que la fila cambie ya, y
                                        // recarga para traer los datos calculados.
                                        setResiduosGenerados(newData);
                                        recargarResiduos();
                                    }}
                                    elementName="Residuo"
                                    sortProp="codResiduoObra"
                                    stateProp="codEstado"
                                    states={[
                                        {
                                            label: "Sin datos",
                                            type: "info",
                                            Icon: CiClock1,
                                        },
                                        {
                                            label: "Pendiente de validar",
                                            type: "info",
                                            Icon: CiClock1,
                                        },
                                        {
                                            label: "Validación con incidencias",
                                            type: "danger",
                                            Icon: CgDanger,
                                        },
                                        {
                                            label: "Validada",
                                            type: "success",
                                            Icon: CiCircleCheck,
                                        },
                                    ]}
                                    keyProp="codResiduoObra"
                                    formElements={[
                                        {
                                            title: "Residuo",
                                            titleVisible: true,
                                            elements: [
                                                {
                                                    name: "codResiduo",
                                                    type: "select",
                                                    label: "Tipo de residuo",
                                                    placeholder:
                                                        "Seleccione el tipo de residuo",
                                                    required: true,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                    options: residuos,
                                                },
                                                {
                                                    name: "codProyecto",
                                                    type: "select",
                                                    label: "Proyecto",
                                                    placeholder:
                                                        "Seleccione proyecto",
                                                    required: false,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                    options: proyectos,
                                                },

                                                ...(isMabe
                                                    ? [
                                                          {
                                                              name: "clasificacion",
                                                              type: "select",
                                                              label: "Clasificación",
                                                              placeholder:
                                                                  "Seleccione clasificación",
                                                              required: true,
                                                              disabled: false,
                                                              labelVisible: true,
                                                              visible: true,
                                                              options: [
                                                                  {
                                                                      value: "1",
                                                                      label: "RR/RR",
                                                                  },
                                                                  {
                                                                      value: "2",
                                                                      label: "RNV",
                                                                  },
                                                              ],
                                                          },
                                                      ]
                                                    : []),
                                                ...(isMabe
                                                    ? [
                                                          {
                                                              name: "estadoFisico",
                                                              type: "select",
                                                              label: "Estado Físico",
                                                              placeholder:
                                                                  "Seleccione estado físico",
                                                              required: true,
                                                              disabled: false,
                                                              labelVisible: true,
                                                              visible: true,
                                                              options: [
                                                                  {
                                                                      value: "1",
                                                                      label: "Sólido",
                                                                  },
                                                                  {
                                                                      value: "2",
                                                                      label: "Líquido",
                                                                  },
                                                                  {
                                                                      value: "3",
                                                                      label: "Gaseoso",
                                                                  },
                                                              ],
                                                          },
                                                      ]
                                                    : []),
                                                ...(isMabe
                                                    ? [
                                                          {
                                                              name: "formaManejo",
                                                              type: "select",
                                                              label: "Forma de Manejo",
                                                              placeholder:
                                                                  "Seleccione forma de manejo",
                                                              required: true,
                                                              disabled: false,
                                                              labelVisible: true,
                                                              visible: true,
                                                              options: [
                                                                  {
                                                                      value: "1",
                                                                      label: "CC - Confinamiento Controlado",
                                                                  },
                                                                  {
                                                                      value: "2",
                                                                      label: "CERS - Celdas Especiales en Rellenos Sanitarios",
                                                                  },
                                                              ],
                                                          },
                                                      ]
                                                    : []),
                                                {
                                                    name: "codObra",
                                                    type: "text",
                                                    label: "CodObra",
                                                    placeholder:
                                                        "Inserte codObra",
                                                    required: true,
                                                    disabled: false,
                                                    labelVisible: false,
                                                    visible: false,
                                                    defaultValue:
                                                        centro.codObra,
                                                },
                                                {
                                                    name: "codZona",
                                                    type: "text",
                                                    label: "CodZona",
                                                    placeholder:
                                                        "Inserte codZona",
                                                    required: false,
                                                    disabled: false,
                                                    labelVisible: false,
                                                    visible: false,
                                                    defaultValue:
                                                        selectedZone.value,
                                                },
                                            ],
                                        },
                                        {
                                            title: "Transporte",
                                            titleVisible: true,
                                            elements: [
                                                {
                                                    /* Un residuo tiene UN transportista:
                                                       ResiduosObra.CodTransportista es una
                                                       sola columna y ResiduosObraDTO una sola
                                                       propiedad. El multiselect en plural que
                                                       habia aqui no existe en el DTO, asi que
                                                       ni se guardaba ni se recuperaba. */
                                                    name: "codTransportista",
                                                    type: "select",

                                                    label: "Transportista",
                                                    placeholder:
                                                        "Seleccione el transportista",
                                                    required: isMabe
                                                        ? true
                                                        : false,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                    options: transportistas,
                                                    associatedFormElement:
                                                        "codResiduo",
                                                },
                                                {
                                                    name: "fechaTransporte",
                                                    type: "date",
                                                    label: "Fecha transporte",
                                                    placeholder:
                                                        "Seleccione la fecha de transporte",
                                                    required: isMabe
                                                        ? true
                                                        : false,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                },
                                                {
                                                    name: "codVehiculo",
                                                    type: "select",
                                                    label: "Tipo de vehículo",
                                                    placeholder:
                                                        "Seleccione el tipo de vehículo",
                                                    required: isMabe
                                                        ? true
                                                        : false,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                    options: vehiculos,
                                                },
                                                {
                                                    name: "tipoCombustible",
                                                    type: "select",
                                                    label: "Tipo de combustible",
                                                    placeholder:
                                                        "Seleccione el tipo de combustible",
                                                    required: false,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                    options: combustibles,
                                                    associatedFormElement:
                                                        "codVehiculo",
                                                },
                                                {
                                                    name: "numKm",
                                                    type: "number",
                                                    label: "Número de Km",
                                                    placeholder:
                                                        "Ingrese el número de kilómetros",
                                                    required: false,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                },
                                                {
                                                    name: "numViajes",
                                                    type: "number",
                                                    label: "Número de viajes",
                                                    placeholder:
                                                        "Ingrese el número de viajes",
                                                    required: false,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                },
                                                ...(isMabe
                                                    ? [
                                                          {
                                                              name: "nombreTransportista",
                                                              type: "text",
                                                              label: "Nombre del transportista",
                                                              placeholder:
                                                                  "Ingrese el nombre del transportista",
                                                              required: true,
                                                              disabled: false,
                                                              labelVisible: true,
                                                              visible: true,
                                                          },
                                                      ]
                                                    : []),
                                                ...(isMabe
                                                    ? [
                                                          {
                                                              name: "numLicenciaTransportista",
                                                              type: "text",
                                                              label: "Número de licencia",
                                                              placeholder:
                                                                  "Ingrese el número de licencia",
                                                              required: true,
                                                              disabled: false,
                                                              labelVisible: true,
                                                              visible: true,
                                                          },
                                                      ]
                                                    : []),
                                                ...(isMabe
                                                    ? [
                                                          {
                                                              name: "tipoLicenciaTransportista",
                                                              type: "text",
                                                              label: "Tipo de licencia",
                                                              placeholder:
                                                                  "Ingrese el tipo de licencia",
                                                              required: true,
                                                              disabled: false,
                                                              labelVisible: true,
                                                              visible: true,
                                                          },
                                                      ]
                                                    : []),
                                                ...(isMabe
                                                    ? [
                                                          {
                                                              name: "numPlacaTransportista",
                                                              type: "text",
                                                              label: "Número de placa",
                                                              placeholder:
                                                                  "Ingrese el número de placa",
                                                              required: true,
                                                              disabled: false,
                                                              labelVisible: true,
                                                              visible: true,
                                                          },
                                                      ]
                                                    : []),
                                                {
                                                    name: "ficheroTransportista",
                                                    type: "document",
                                                    label: "Documento transporte",
                                                    placeholder:
                                                        "Seleccione el documento de transporte",
                                                    required: false,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                },
                                            ],
                                        },
                                        {
                                            title: "Gestión intermedia",
                                            titleVisible: true,
                                            elements: [
                                                {
                                                    name: "codGestorIntermedio",
                                                    type: "select",

                                                    label: "Gestor intermedio",
                                                    placeholder:
                                                        "Seleccione el gestor intermedio",
                                                    required: false,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                    options: gestores,
                                                    associatedFormElement:
                                                        "codResiduo",
                                                },
                                                {
                                                    name: "fechaGestionIntermedia",
                                                    type: "date",
                                                    label: "Fecha gestión intermedia",
                                                    placeholder:
                                                        "Seleccione la fecha de gestión intermedia",
                                                    required: false,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                },
                                                {
                                                    name: "tipoTratamiento",
                                                    type: "select",
                                                    label: "Tipo de tratamiento",
                                                    placeholder:
                                                        "Seleccione el tipo de tratamiento",
                                                    required: false,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                    options: [
                                                        {
                                                            value: "Eliminación",
                                                            label: "Eliminación",
                                                        },
                                                        {
                                                            value: "Valorización",
                                                            label: "Valorización",
                                                        },
                                                    ],
                                                },
                                                {
                                                    name: "codTipoTratamiento",
                                                    type: "select",
                                                    label: "Descripción tipo tratamiento",
                                                    placeholder:
                                                        "Ingrese la descripción del tipo de tratamiento",
                                                    required: false,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                    options: tiposTratamiento,
                                                    associatedFormElement:
                                                        "tipoTratamiento",
                                                },
                                                {
                                                    name: "porcentaje",
                                                    type: "number",
                                                    label: "Porcentaje",
                                                    placeholder:
                                                        "Ingrese el porcentaje",
                                                    required: false,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                },
                                            ],
                                        },
                                        {
                                            title: "Gestión final",
                                            titleVisible: true,
                                            elements: [
                                                {
                                                    name: "codGestorFinal",
                                                    type: "select",
                                                    label: "Gestor final",
                                                    placeholder:
                                                        "Seleccione el gestor final",
                                                    required: true,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                    options: gestores,
                                                    associatedFormElement:
                                                        "codResiduo",
                                                },
                                                {
                                                    name: "fechaTratamiento",
                                                    type: "date",
                                                    label: "Fecha gestión final",
                                                    placeholder:
                                                        "Seleccione la fecha de gestión final",
                                                    required: false,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                },
                                                {
                                                    name: "tipoTratamiento2",
                                                    type: "select",
                                                    label: "Tipo de tratamiento",
                                                    placeholder:
                                                        "Seleccione el tipo de tratamiento",
                                                    required: false,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                    options: [
                                                        {
                                                            value: "Eliminación",
                                                            label: "Eliminación",
                                                        },
                                                        {
                                                            value: "Valorización",
                                                            label: "Valorización",
                                                        },
                                                    ],
                                                },
                                                {
                                                    name: "codTipoTratamiento2",
                                                    type: "select",
                                                    label: "Descripción tipo tratamiento",
                                                    placeholder:
                                                        "Ingrese la descripción del tipo de tratamiento",
                                                    required: false,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                    options: tiposTratamiento,
                                                    associatedFormElement:
                                                        "tipoTratamiento2",
                                                },
                                                {
                                                    name: "porcentaje2",
                                                    type: "number",
                                                    label: "Porcentaje",
                                                    placeholder:
                                                        "Ingrese el porcentaje",
                                                    required: false,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                },
                                                ...(isMabe
                                                    ? [
                                                          {
                                                              name: "tipoVehiculoGestorFinal",
                                                              type: "select",
                                                              label: "Tipo de vehículo",
                                                              placeholder:
                                                                  "Seleccione el tipo de vehículo",
                                                              required: true,
                                                              disabled: false,
                                                              labelVisible: true,
                                                              visible: true,
                                                              options:
                                                                  vehiculos,
                                                          },
                                                      ]
                                                    : []),
                                                ...(isMabe
                                                    ? [
                                                          {
                                                              name: "numPlacaGestorFinal",
                                                              type: "text",
                                                              label: "Número de placa",
                                                              placeholder:
                                                                  "Ingrese el número de placa",
                                                              required: true,
                                                              disabled: false,
                                                              labelVisible: true,
                                                              visible: true,
                                                          },
                                                      ]
                                                    : []),
                                                ...(isMabe
                                                    ? [
                                                          {
                                                              name: "nombreGestorFinal",
                                                              type: "text",
                                                              label: "Nombre del gestor final",
                                                              placeholder:
                                                                  "Ingrese el nombre del gestor final",
                                                              required: true,
                                                              disabled: false,
                                                              labelVisible: true,
                                                              visible: true,
                                                          },
                                                      ]
                                                    : []),
                                                ...(isMabe
                                                    ? [
                                                          {
                                                              name: "cargoGestorFinal",
                                                              type: "text",
                                                              label: "Cargo gestor final",
                                                              placeholder:
                                                                  "Ingrese el cargo del gestor final",
                                                              required: true,
                                                              disabled: false,
                                                              labelVisible: true,
                                                              visible: true,
                                                          },
                                                      ]
                                                    : []),
                                                {
                                                    name: "observacionesGestionFinal",
                                                    type: "textarea",
                                                    label: "Observaciones gestión final",
                                                    placeholder:
                                                        "Ingrese las observaciones de la gestión final",
                                                    required: false,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                },
                                                {
                                                    name: "ficheroGestorFinal",
                                                    type: "document",
                                                    label: "Documento gestor final",
                                                    placeholder:
                                                        "Seleccione el documento del gestor final",
                                                    required: false,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                },
                                            ],
                                        },
                                        {
                                            title: "Cantidades generadas",
                                            titleVisible: true,
                                            elements: [
                                                {
                                                    name: "peso",
                                                    type: "number",
                                                    label: "Peso (kg)",
                                                    labelDinamica:
                                                        etiquetaPesoResiduo,
                                                    placeholder:
                                                        "Ingrese el peso",
                                                    required: true,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                },
                                                {
                                                    name: "volumen",
                                                    type: "number",
                                                    label: "Volumen (m3)",
                                                    placeholder:
                                                        "Ingrese el volumen",
                                                    required: false,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                },
                                                ...(isMabe
                                                    ? [
                                                          {
                                                              name: "capacidad",
                                                              type: "text",
                                                              label: "Capacidad",
                                                              placeholder:
                                                                  "Ingrese la capacidad",
                                                              required: true,
                                                              disabled: false,
                                                              labelVisible: true,
                                                              visible: true,
                                                          },
                                                      ]
                                                    : []),
                                                ...(isMabe
                                                    ? [
                                                          {
                                                              name: "tipo",
                                                              type: "text",
                                                              label: "Tipo",
                                                              placeholder:
                                                                  "Ingrese el tipo",
                                                              required: true,
                                                              disabled: false,
                                                              labelVisible: true,
                                                              visible: true,
                                                          },
                                                      ]
                                                    : []),
                                            ],
                                        },
                                        {
                                            title: "Coste",
                                            titleVisible: true,
                                            elements: [
                                                {
                                                    name: "costeTransporte",
                                                    type: "number",
                                                    label: "Transporte",
                                                    placeholder:
                                                        "Ingrese el coste de transporte",
                                                    required: false,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                },
                                                {
                                                    name: "costeGestion",
                                                    type: "number",
                                                    label: "Gestión",
                                                    placeholder:
                                                        "Ingrese el coste de gestión",
                                                    required: false,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                },
                                                {
                                                    name: "costeColocacion",
                                                    type: "number",
                                                    label: "Colocación contenedor",
                                                    placeholder:
                                                        "Ingrese el coste de colocación de contenedor",
                                                    required: false,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                },
                                                {
                                                    name: "costeAlquiler",
                                                    type: "number",
                                                    label: "Alquiler contenedor",
                                                    placeholder:
                                                        "Ingrese el coste de alquiler de contenedor",
                                                    required: false,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                },
                                                {
                                                    name: "costeDocumento",
                                                    type: "number",
                                                    label: "Generación documento",
                                                    placeholder:
                                                        "Ingrese el coste de generación de documento",
                                                    required: false,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                },
                                                {
                                                    /* Es un ingreso por la venta de
                                                       chatarra: se introduce en
                                                       negativo y resta del coste
                                                       total, como en la aplicación
                                                       antigua (el input tenía
                                                       max="0"). */
                                                    name: "abonoChatarra",
                                                    type: "number",
                                                    label: "Abono chatarra",
                                                    placeholder:
                                                        "Ingrese el abono en negativo",
                                                    required: false,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                },
                                            ],
                                        },
                                    ]}
                                    stateChangeOptions={[
                                        {
                                            value: "1",
                                            label: "Pendiente de validar",
                                        },
                                        {
                                            value: "2",
                                            label: "Validación con incidencias",
                                        },
                                        { value: "3", label: "Validada" },
                                    ]}
                                    /* Sin permiso para validar no se pasa la URL: así la tabla no
                                       ofrece el cambio de estado ni el botón de validar en bloque. */
                                    stateChangeUrl={
                                        puedeValidar
                                            ? "/api/Validaciones/SetValidacion"
                                            : undefined
                                    }
                                    addApiUrl="/api/ResiduosObra/SetResiduoObra"
                                    editApiUrl="/api/ResiduosObra/UpdateResiduoObra"
                                    deleteApiUrl="/api/ResiduosObra/DeleteResiduoObra"
                                    /* Siempre multipart y con el peso en kg: el
                                       formulario lo pide en tn para los no
                                       peligrosos. */
                                    payloadMapper={residuoFormAPayload}
                                    rowToFormMapper={residuoFilaAFormulario}
                                    validateForm={validaResiduoForm}
                                    onRowClick={(row) =>
                                        setResiduoDetalle(row)
                                    }
                                    searchTerms={[
                                        "residuo/nombre",
                                        "fechaRecogida",
                                        "peso",
                                        "costeFinal",
                                    ]}
                                    loading={loading}
                                    userCodAcceso={Number(user.codAcceso)}
                                    userCodPerfil={Number(user.role)}
                                    exportApiUrl="/api/ResiduosObra/obtener-informe1-mabe"
                                    exportActionLabel="Generar manifiesto"
                                    extraPropExportKey="codObra"
                                    extraPropExportText="codObra"
                                    excludeFields={[
                                        "observacionesGestionFinal", 
                                        "residuo/nombre", 
                                        "fechaRecogida", 
                                        "codResiduoObra", 
                                        "nombreObra", 
                                        "codTipoObra", 
                                        "codProductor", 
                                        "codPais", 
                                        "codResiduo", 
                                        "codProyecto", 
                                        "codEstadoFisico", 
                                        "codPeligrosidad", 
                                        "codTipoResiduo", 
                                        "codIdentificacion", 
                                        "codTipoGestion", 
                                        "codTransportista", 
                                        "documentoTransporte", 
                                        "fechaSubida", 
                                        "fechaGestionIntermedia", 
                                        "unidadPeso", 
                                        "codVehiculo", 
                                        "codCombustible", 
                                        "codGestorFinal", 
                                        "codGestorIntermedio", 
                                        "codTipoTratamiento", 
                                        "codTipoTratamiento2", 
                                        "codPaisGestorFinal", 
                                        "codPaisGestorIntermedio", 
                                        "codPaisTransportista", 
                                        "codProvinciaGestorFinal", 
                                        "codProvinciaGestorIntermedio", 
                                        "codProvinciaTransportista", 
                                        "documentoFinal", 
                                        "documento", 
                                        "alquiler", 
                                        "colocacion", 
                                        "autorizacionGestorFinal", 
                                        "fechaFinObra", 
                                        "ficheroTransportista", 
                                        "ficheroGestorFinal", 
                                        "lastValidationNote", 
                                        "codEstado", 
                                        "autorizacionTransportista", 
                                        "eliminacion", 
                                        "valorizacion", 
                                        "pesoConUnidad", 
                                        "porcentaje", 
                                        "porcentaje2", 
                                        "numDocumentoFinal", 
                                        "numDocumentoTransporte", 
                                        "costeFinal", 
                                        "numKm", 
                                        "numViajes"]}
                                />
                            </div>

                            <div
                                style={{
                                    display:
                                        activeTabResiduos === "tarifas"
                                            ? "block"
                                            : "none",
                                }}
                            >
                                <Table
                                    columns={[
                                        {
                                            key: "gestor",
                                            label: "Gestor",
                                            visible: true,
                                            sortable: true,
                                        },
                                        {
                                            key: "tarifa",
                                            label: "Tarifa",
                                            visible: true,
                                            sortable: false,
                                        },
                                    ]}
                                    keyProp="codTarifa"
                                    dataTable={tarifas}
                                    noResultsMessage="No hay tarifas disponibles"
                                    rowsPerPage={10}
                                    showCheckboxes={false}
                                    elementName="Tarifa"
                                    searchTerms={["gestor", "tarifa"]}
                                    showAdvancedFilters={false}
                                    userCodAcceso={Number(user.codAcceso)}
                                    userCodPerfil={Number(user.role)}
                                    loading={loading}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="ficha-centro__section">
                        <div className="ficha-centro__section-header">
                            <h2>Documentación</h2>
                        </div>
                        <TabsNavigation
                            tabs={tabsDocumentacion}
                            activeTab={activeTabDocumentacion}
                            onTabChange={setActiveTabDocumentacion}
                        />
                        <div className="ficha-centro__section-content">
                            {renderDocumentacionContent()}
                        </div>
                    </div>

                    <div className="ficha-centro__section">
                        <div className="ficha-centro__section-header ficha-centro__section-header--with-action">
                            <h2>Consumo</h2>
                            {puedeGestionarCentro && (
                                <Button
                                    variant="filled"
                                    startIcon={<IoMdAdd />}
                                    onClick={() =>
                                        consumoTableRef.current?.openAddModal()
                                    }
                                >
                                    Agregar Consumo
                                </Button>
                            )}
                        </div>
                        <div className="ficha-centro__section-content">
                            {renderConsumo()}
                        </div>
                    </div>
                    <Modal
                        isOpen={isModalBitacoraOpen}
                        onClose={() => {
                            setIsModalBitacoraOpen(false);
                            setSelectedMonthBitacora(null);
                        }}
                        className="modal--dropdown-visible"
                        title="Seleccione el mes de la bitácora a exportar"
                        actions={[
                            <Button
                                onClick={() => handleBitacoraResiduo()}
                                startIcon={<FiFileText />}
                            >
                                Generar informe
                            </Button>,
                        ]}
                    >
                        <InputDropdown
                            label="Mes de la bitácora"
                            placeholder="Seleccione el mes"
                            options={[
                                { value: 1, label: "Enero" },
                                { value: 2, label: "Febrero" },
                                { value: 3, label: "Marzo" },
                                { value: 4, label: "Abril" },
                                { value: 5, label: "Mayo" },
                                { value: 6, label: "Junio" },
                                { value: 7, label: "Julio" },
                                { value: 8, label: "Agosto" },
                                { value: 9, label: "Septiembre" },
                                { value: 10, label: "Octubre" },
                                { value: 11, label: "Noviembre" },
                                { value: 12, label: "Diciembre" },
                            ]}
                            defaultValue={selectedMonthBitacora}
                            onChange={(value) =>
                                setSelectedMonthBitacora(value)
                            }
                            multiple={false}
                        />
                    </Modal>
                    <Modal
                        isOpen={modalEdicionAbierto}
                        onClose={() => setModalEdicionAbierto(false)}
                        title="Editar centro"
                        actions={[
                            <Button
                                key="guardar"
                                onClick={() => handleGuardarEdicionCentro()}
                                startIcon={<FiEdit />}
                            >
                                Guardar cambios
                            </Button>,
                        ]}
                    >
                        <div className="ficha-centro__edit-form">
                            <div className="ficha-centro__field">
                                <label>Código Espacio</label>
                                <input
                                    value={formEdicionCentro.codEspacio || ""}
                                    onChange={(e) =>
                                        handleCambioEdicionCentro(
                                            "codEspacio",
                                            e.target.value
                                        )
                                    }
                                />
                            </div>
                            <div className="ficha-centro__field">
                                <label>Nombre</label>
                                <input
                                    value={formEdicionCentro.nombre || ""}
                                    onChange={(e) =>
                                        handleCambioEdicionCentro(
                                            "nombre",
                                            e.target.value
                                        )
                                    }
                                />
                            </div>
                            <div className="ficha-centro__field">
                                <label>Dirección</label>
                                <input
                                    value={formEdicionCentro.direccion || ""}
                                    onChange={(e) =>
                                        handleCambioEdicionCentro(
                                            "direccion",
                                            e.target.value
                                        )
                                    }
                                />
                            </div>
                            <div className="ficha-centro__field">
                                <label>Localidad</label>
                                <input
                                    value={formEdicionCentro.localidad || ""}
                                    onChange={(e) =>
                                        handleCambioEdicionCentro(
                                            "localidad",
                                            e.target.value
                                        )
                                    }
                                />
                            </div>
                            <div className="ficha-centro__field">
                                <label>Código Integración</label>
                                <input
                                    value={
                                        formEdicionCentro.codIntegracion || ""
                                    }
                                    onChange={(e) =>
                                        handleCambioEdicionCentro(
                                            "codIntegracion",
                                            e.target.value
                                        )
                                    }
                                />
                            </div>
                            <div className="ficha-centro__field">
                                <label>Promotor</label>
                                <input
                                    value={formEdicionCentro.promotor || ""}
                                    onChange={(e) =>
                                        handleCambioEdicionCentro(
                                            "promotor",
                                            e.target.value
                                        )
                                    }
                                />
                            </div>
                            <div className="ficha-centro__field">
                                <label>Responsable</label>
                                <input
                                    value={formEdicionCentro.responsable || ""}
                                    onChange={(e) =>
                                        handleCambioEdicionCentro(
                                            "responsable",
                                            e.target.value
                                        )
                                    }
                                />
                            </div>
                            <div className="ficha-centro__field">
                                <label>Mail Responsable</label>
                                <input
                                    value={
                                        formEdicionCentro.mailResponsable || ""
                                    }
                                    onChange={(e) =>
                                        handleCambioEdicionCentro(
                                            "mailResponsable",
                                            e.target.value
                                        )
                                    }
                                />
                            </div>
                            <div className="ficha-centro__field">
                                <label>Observaciones</label>
                                <textarea
                                    rows={3}
                                    value={
                                        formEdicionCentro.observaciones || ""
                                    }
                                    onChange={(e) =>
                                        handleCambioEdicionCentro(
                                            "observaciones",
                                            e.target.value
                                        )
                                    }
                                />
                            </div>
                        </div>
                    </Modal>

                    {/* GetObra trae `validaciones` en el payload, pero se pide al
                        abrir el modal: si el usuario acaba de validar, la lista de
                        la carga inicial estaría desactualizada. */}
                    <ResiduoDetalleModal
                        isOpen={residuoDetalle != null}
                        onClose={() => setResiduoDetalle(null)}
                        residuo={residuoDetalle}
                        catalogos={{
                            proyectos,
                            vehiculos,
                            combustibles,
                            tiposTratamiento,
                        }}
                        formateaCoste={formateaCoste}
                    />

                    <ValidacionesHistorico
                        isOpen={isHistoricoOpen}
                        onClose={() => setIsHistoricoOpen(false)}
                        codTipoValidacion={
                            historicoValidacion?.codTipoValidacion
                        }
                        codClave={historicoValidacion?.codClave}
                    />

                    <Notification ref={notificationRef} />
                </div>
            )}
        </DetalleLayout>
    );
}
